import { Grid, Stack } from '@mui/material'
import { FC } from 'react'
import { useShowContext, useTranslate } from 'react-admin'
import { Card, Muted, Property, PropertyDivider } from '../../components/ui'
import { CertificateOut, CertificateStatus, TargetSystem } from '../../types/apiRequests'
import CertificateLogs from './CertificateLogs'
import CertificateSchedules from './CertificateSchedules'
import ActionsByStatus from './CertificateActionsByStatus'
import ExpiryAlert from './CertificateExpiryAlert'
import { isExpired } from './CertificatePills'
import ZakenDetails from './details/Zaken.details'
import { getCommonName } from '../../utils/getCommonName'
import { getCapabilities, useCertificateLabels } from './targetSystems'

const dash = <Muted>-</Muted>

/** Main column (next step, schedule, log) plus properties side panel; stacks below lg. */
const CertificateForm: FC = () => {
  const translate = useTranslate()
  const { record } = useShowContext<CertificateOut>()

  const { system, type } = useCertificateLabels(record)
  const certificateIsExpired = isExpired(record)
  const showExpiry = record?.status === CertificateStatus.ACTIVE ||
    record?.status === CertificateStatus.EXPIRED ||
    certificateIsExpired
  const hostname = getCommonName(record)

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Stack spacing={3}>
          <Card title={translate('resources.certificates.detail.nextStep')}>
            <Stack spacing={2}>
              {showExpiry && <ExpiryAlert />}
              <ActionsByStatus />
            </Stack>
          </Card>

          {/* Bezwaren/Koppelen have no installation, so no schedule section. */}
          {getCapabilities(record?.targetSystem)?.installable && (
            <Card title={translate('data.schedule')} delay={60}>
              <CertificateSchedules certificateId={record?.id} />
            </Card>
          )}

          <Card title={translate('data.log')} delay={120}>
            <CertificateLogs record={record} />
          </Card>
        </Stack>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Card title={translate('resources.certificates.fields.properties')} delay={40}>
          <Property label={translate('resources.certificates.fields.targetSystem')}>
            {system || dash}
          </Property>
          <Property label={translate('resources.certificates.fields.type')}>
            {type || dash}
          </Property>
          {record?.targetSystem === TargetSystem.ZAKEN && <ZakenDetails />}
          <Property label={translate('resources.certificates.fields.host')}>
            {hostname ? <Muted mono plain>{hostname}</Muted> : dash}
          </Property>

          <PropertyDivider />

          <Property label={translate('data.contactpersoon')}>{record?.contactNaam || dash}</Property>
          <Property label={translate('data.email')}>{record?.contactEmail || dash}</Property>
          <Property label={translate('data.phone')}>{record?.contactTelefoon || dash}</Property>
        </Card>
      </Grid>
    </Grid>
  )
}

export default CertificateForm
